import { ControlType, IEntity, Property, PropertyType } from "../../../base/types";
import { FC, useEffect, useState } from "react";
import { IControlAttributes, IPageLink } from "../../../base/models";
import { Registry } from "../../../base/register";
import { useGetProperties } from "../../../base/hooks";
import { useLazyGetBoardingPassQuery } from "./properties/boardingPassApi";
import moment from "moment";
import { useAuth } from "../../../../modules/auth";
import { Spinner } from "../../../widgets";
import { useUpdateMutation } from "../../../services/data/tableDataApi";
import { IFieldValue } from "../../../services/models";
import { useNavigate } from "react-router-dom";
import { PageLinkHelper } from "../../../base/helpers";
import { useGetLookupNames } from "./properties/useGetLookupNames";
import { DownloadLinkCell } from "../downloadable-document";


const useGetNameFromGuid = (guid: string) => {
    const { getLookupLabel } = useGetLookupNames();
    const [name, setName] = useState<string | undefined>();

    useEffect(() => {
        if (guid) {
            getLookupLabel({ value: guid })
                .then(fetchedName => setName(fetchedName))
                .catch(err => console.error('Error fetching name:', err));
        }
    }, [guid, getLookupLabel]);

    return name;
};

export const BoardingPassComponent: FC<IControlAttributes> = ({
    control,
    className,
    children,
    props,
    designerMode,
    ...rest
}) => {
    const { properties } = useGetProperties({ bindings: control.bindings });
    const navigateToAbcId: IPageLink | undefined = properties[Property.CancelPageLink];
    const { user, siteId } = useAuth();
    const navigate = useNavigate();
    const [updateRecord] = useUpdateMutation();
    const [getBoardingPass, { data: BoardingPass }] = useLazyGetBoardingPassQuery();
    const [loading, setLoading] = useState(false);


    useEffect(() => {
        if (user?.contactId) {
            getBoardingPass({ studentId: user.contactId });
        }
        // eslint-disable-next-line
    }, [getBoardingPass, navigateToAbcId]);

    const navigateToAbcIdPage = () => {
        if (siteId && navigateToAbcId && navigateToAbcId.pageId) {
            navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateToAbcId));
        }
    }

    const handleUpdateAbcID = () => {
        const record: IEntity = {
            edtec365_generatedon: true,
        };
        setLoading(true)
        saveFormData(record)
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                console.error(message);
            }).finally(() => {
                setLoading(false)
            })
    };

    const saveFormData = (record: IEntity): Promise<IEntity> => {
        const fields: IFieldValue[] = Object.entries(record)
            .filter(([_, value]) => value !== undefined)
            .map(([field, value]) => ({ field, value, validate: true }));

        return updateRecord({ portalId: siteId || '', userId: user?.contactId, tableId: "edtec365_boardingpass", sysId: BoardingPass?.id || '', data: fields, triggers: true })
            .unwrap()
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                console.error(message);
                return Promise.reject(message);
            });
    };

    return (
        <div className="container bg-white rounded">
            <div className="container mt-5">
                <div className="row border border-dark rounded wrap p-3">
                    <div className="col-md-6">
                        <div className="d-flex flex-column gap-2">
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Student JGU ID:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{BoardingPass?.studentJguId}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Batch:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{useGetNameFromGuid(BoardingPass?.batch || '')}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Academic Session:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{BoardingPass?.academicSessionCopy}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Program:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{useGetNameFromGuid(BoardingPass?.program || '')}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Reporting Gate:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{BoardingPass?.reportingGate}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Face Syncing:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>
                                        {BoardingPass?.faceSyncing ? "Yes" : (
                                            BoardingPass?.studenttype === 365970001 ? (
                                                <span className="text-danger">Please get in touch with the Biometric Team</span>
                                            ) : "No"
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Tuition Fee Paid:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>
                                        {BoardingPass?.tuitionFeePaid ? "Yes" : (
                                            <span className="text-danger">Kindly pay your fee or contact finance department</span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="d-flex flex-column gap-2">
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Student:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{useGetNameFromGuid(BoardingPass?.student || '')}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Academic Year:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{useGetNameFromGuid(BoardingPass?.academicYear || '')}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Semester:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{useGetNameFromGuid(BoardingPass?.semester || '')}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Email ID:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{BoardingPass?.emailId}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Reporting Date:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>{moment(BoardingPass?.reportingDate).format('DD-MM-YYYY')}</span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>ABC ID:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>
                                        {BoardingPass?.abcid ? "Yes" : (
                                            <>
                                                <span className="text-danger">Kindly fill your ABC ID</span>
                                                <button className="btn btn-link text-primary mx-1" onClick={navigateToAbcIdPage}>
                                                    Click Here
                                                </button>
                                            </>
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-5">
                                    <strong>Hostel Fee Paid:</strong>
                                </div>
                                <div className="col-md-6">
                                    <span>
                                        {BoardingPass?.hostelFeePaid ? "Yes" : (
                                            <span className="text-danger">
                                                Kindly pay your hostel fee or contact GHS/JGU finance department
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {BoardingPass?.boardingPassUrl && <div className="text-end mt-4">
                    <div className="d-inline-block btn btn-primary btn-sm" onClick={handleUpdateAbcID}>
                        {loading && <Spinner className="me-2" show={loading} />}
                        <DownloadLinkCell
                            fieldUrl={BoardingPass?.boardingPassUrl || ''}
                            label="Generate Boarding Pass"
                            target="_blank"
                            className="text-white"
                            enableDownload={true}
                        />
                    </div>
                </div>}
            </div>
        </div>
    );
};

Registry.addControl({
    id: "5da149c8-904b-4846-9714-398d8f5aca24",
    name: "Boarding Pass Component",
    type: ControlType.BoardingPassComponent,
    properties: [
        { id: Property.Tooltip, name: "Tooltip", type: PropertyType.String },
        { id: Property.Label, name: "Navigate Label", type: PropertyType.String },
        { id: Property.CancelPageLink, name: 'Navigate To ABC Id', type: PropertyType.PageLink },
    ],
    component: BoardingPassComponent,
});
